/* global views, showModalError, functionality, components, rulesFlow  */

import React, { useState, useEffect, useContext, useCallback } from "react";
import styled from "styled-components";
import Request from "common/Request";
import FilteringControls from "./FilteringControls";
import ActionsContext from "common/ActionsContext";
import { flowsRetrieval, flowsUsageRetrieval, parseTotalFlows } from "./api";
import help from "help/viewFlowsPerSubs.md";
import useProfile from "common/hooks/useProfile";
import View from "common/View";
import { atoiv6, atoiv4, durationToInt} from "common/utils";
import BqnContext from "common/BqnContext";
import Table from "common/graphs/Table/Table";
import { NumberCell, TimeCell } from "common/graphs/Table/TableRenders";

const doesNothing = () => {};

const useFlowsData = ({ lines, subnet, policy }) => {
  const [request, setRequest] = useState(null);
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;

  const doLoad = useCallback( ()=>{
    setRequest(doRequest({lines, subnet, policy, bqnId, mode}));
  } , [lines, subnet, policy, bqnId, mode])
  return [request, doLoad];
}

const Report = ({
  setTotal = doesNothing,
  subnet,
  lines,
  policy,
  ..._otherSettings
}) => {
  const actions = useContext(ActionsContext);
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const [request, doLoad] = useFlowsData({lines, subnet, policy});

  const doLoadTotals = () =>
    flowsUsageRetrieval(mode, bqnId)
      .then(response => parseTotalFlows(response, bqnId))
      .then(setTotal);

  const update = () => {
    doLoad();
    doLoadTotals();
  };

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. The effect runs again when it does.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    update();
    return actions.recv("do-load", () => {
      update();
    });
  }, [mode, bqnId, lines, subnet, policy]);

  if (request === null) {
    return null;
  }

  return (
    <Request during={request}>{(result) => <ShowFlows {...result} />}</Request>
  );
};

const ShowTotal = ({ total = null }) =>
  total === null ? null : (
    <div className="row">
      <div className="margin-l-0 col-lg-12 col-md-12 col-sm-12 col-xs-12">
        <span>Total Active flows&nbsp;&nbsp;</span>
        <span>{total}</span>
      </div>
    </div>
  );

const FlowsPerSubscriber = ({ returnView, policy }) => {
  const [total, setTotal] = useState(null);
  return (
    <View
      title="Flows Per Subscriber"
      returnView={returnView}
      mdHelp={help}
    >
      <ShowTotal total={total} />
      <FilteringControls policy={policy}>
        {(settings) => <Report {...settings} setTotal={setTotal} />}
      </FilteringControls>
    </View>
  );
};

export default FlowsPerSubscriber;

const doRequest = ({ subnet, lines, policy, bqnId, mode }) =>
  flowsRetrieval({ subnet, lines, policy, mode, bqnId })
    .then((response) => ({ response }));


const openSubscriberDetails = ({ addr, subsId }) => {
  globalNavigate("viewSubsDetail", {
    addr,
    subsId,
    returnView: "viewFlowsPerSubs"
  });
};

const doEditPolicy = (policy) => {
  globalNavigate("viewPolicyFlowEdit", {returnView: "viewFlowsPerSubs", policy})
}

const getColumns = ({profile, mode}) => {
  const { hideRates = false, dpiEnabled = false } = profile;
  return [
    ...(mode !== "standalone"
      ? [
          {
            label: "BQN",
            id: "BQN",
            numeric: true,
          },
        ]
      : []),
      {
        label: "SUBS-ID",
        id: "SUBSCRIBER-ID",
        numeric: false,
        cellClassName: "hyperlink-text",
        onClick: (subsId) => openSubscriberDetails({ subsId }),
      },
      {
        label: "TYPE",
        id: "TYPE",
        numeric: false,
      },
      {
        label: "ACC-ADDR",
        id: "ACC-ADDR",
        numeric: false,
        cellClassName: "hyperlink-text",
        onClick:(addr) => openSubscriberDetails({ addr }),
        parse: (value) => {
          if(!value){
            return 0;
          }
          return value.includes(":") ? atoiv6(value) : atoiv4(value)
        },
        parseFilter: (value) => value
      },
      {
        label: "APORT",
        id: "APORT",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} thousands={false} hasNA={true} />
        ),
      },
      {
        label: "INET-ADDR",
        id: "INET-ADDR",
        numeric: false,
        parse: (value) => {
          if(!value){
            return 0;
          }
          return value.includes(":") ? atoiv6(value) : atoiv4(value)
        },
        parseFilter: (value) => value
      },
      {
        label: "IPORT",
        id: "IPORT",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} thousands={false} hasNA={true} />
        ),
      },
      {
        label: "PROTO",
        id: "PROTO",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} hasNA={true} />
        ),
      },
      {
        label: "ST",
        id: "ST",
        numeric: false,
      },
      {
        label: "INS",
        id: "INS",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} hasNA={false} precision={0}/>
        ),
      },
      {
        label: "FLOW-POLICY",
        id: "POLICY-FLOW",
        numeric: false,
        hidden: !dpiEnabled,
        onClick: hideRates ? null : doEditPolicy,
        cellClassName: hideRates ? null : "hyperlink-text",
      },
      {
        label: "MBYTES-DN",
        id: "MBYTES-DN",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} precision={3} />
        ),
      },
      {
        label: "MBYTES-UP",
        id: "MBYTES-UP",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} precision={3} />
        ),
      },
      {
        label: "LIFETIME",
        id: "LIFETIME",
        renderCell: (value) => <TimeCell value={value} />,
        numeric: true,
        parse: (data) => (data === "n/a" ? null : durationToInt(data)),
      },
      {
        label: "DPI-DOMAIN",
        id: "DOMAIN",
        hidden: !dpiEnabled,
        numeric: false,
      },
  ];
};

/*****************************************************
 * Flows Table
 * ****************************************************/
const ShowFlows = ({ response }) => {
  const profile = useProfile();
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;

  if(profile === null){
    return null;
  }

  const [header, ...rows] = response.trim().split("\n");
  const headerAttrs = header.trim().split(/\s+/);
  const newRows = rows.map((row) => {
    const rowAttrs = row.trim(/\s/).split(/\s+/);
    const newEntry = rowAttrs.reduce((acc, attr, index) => {
      acc[headerAttrs[index]] = attr;
      return acc;
    }, {});
    return newEntry;
  });

  const columns =getColumns({profile, mode});

  return (
  <>
    <TableContainer>
       <Table
          isSelectable={false}
          isRowSelectable={false}
          data={newRows}
          sort={true}
          columns={columns}
          search={true}
          csvPrefix="flows-per-subscriber"
          rowsPerPageNr={50}
        />
    </TableContainer>
  </>
);
}

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
`;
